/**
 * A route to follow, and following it: where along it the traveller is, the
 * point to steer for, and how fast to go there.
 *
 * Routes come from OSRM (see `loaders/routeService.ts`) as a line of
 * latitudes and longitudes with a speed per stretch and the junctions on the
 * way. Here they are laid out flat, in metres east and north of the route's
 * own start, so a rebase of the world's origin never moves them.
 *
 * Pure: no network, no Three.
 */

import type { GeoOrigin } from '../geo/enu.js';
import { enuToGeo, geoToEnu, makeOrigin } from '../geo/enu.js';

/** A junction the route turns at: stop at the line before it, or slow and give way. */
export interface Junction {
  /** Metres along the route. */
  at: number;
  stop: boolean;
}

export interface Route {
  origin: GeoOrigin;
  /** Points as east, north pairs, metres from `origin`. */
  points: Float64Array;
  /** Metres along the route at each point. */
  along: Float64Array;
  /** The speed to drive each stretch, m/s: its limit, else the router's estimate. NaN where neither says. */
  speeds: Float32Array;
  junctions: Junction[];
  length: number;
}

export interface RoutePoint {
  lat: number;
  lon: number;
}

/** Lay out a route from its points, speeds (one per stretch) and junctions (as positions). */
export function makeRoute(points: readonly RoutePoint[], speeds: readonly number[] = [],
  junctions: ReadonlyArray<{ lat: number; lon: number; stop: boolean }> = []): Route {
  const origin = makeOrigin(points[0].lat, points[0].lon);
  const flat = new Float64Array(points.length * 2);
  const along = new Float64Array(points.length);
  points.forEach((p, i) => {
    const e = geoToEnu(origin, p.lat, p.lon);
    flat[i * 2] = e.east;
    flat[i * 2 + 1] = e.north;
    if (i > 0) along[i] = along[i - 1] + Math.hypot(e.east - flat[i * 2 - 2], e.north - flat[i * 2 - 1]);
  });
  const route: Route = {
    origin, points: flat, along,
    speeds: Float32Array.from({ length: Math.max(0, points.length - 1) }, (_, i) => speeds[i] ?? NaN),
    junctions: [], length: along[points.length - 1] ?? 0,
  };
  route.junctions = junctions
    .map(j => { const e = geoToEnu(origin, j.lat, j.lon); return { at: project(route, e.east, e.north, 0, route.length).along, stop: j.stop }; })
    .sort((a, b) => a.at - b.at);
  return route;
}

/** A position in the route's frame. */
export function onRoute(route: Route, lat: number, lon: number): { east: number; north: number } {
  return geoToEnu(route.origin, lat, lon);
}

/** A route position back to latitude and longitude. */
export function routeToGeo(route: Route, east: number, north: number): RoutePoint {
  return enuToGeo(route.origin, east, north);
}

/**
 * The nearest point of the route to (east, north), searching only between
 * `from` and `to` metres along it — so a route that doubles back on itself is
 * followed in order, not jumped along.
 */
export function project(route: Route, east: number, north: number, from: number, to: number): { along: number; off: number } {
  const p = route.points, a = route.along;
  let best = { along: from, off: Infinity };
  for (let i = 0; i + 1 < a.length; i++) {
    if (a[i + 1] < from || a[i] > to) continue;
    const x0 = p[i * 2], y0 = p[i * 2 + 1], dx = p[i * 2 + 2] - x0, dy = p[i * 2 + 3] - y0;
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((east - x0) * dx + (north - y0) * dy) / l2)) : 0;
    const d = Math.hypot(x0 + dx * t - east, y0 + dy * t - north);
    if (d < best.off) best = { along: a[i] + (a[i + 1] - a[i]) * t, off: d };
  }
  return best;
}

/** The point `s` metres along the route, and the direction it runs there (unit east, north). */
export function pointAt(route: Route, s: number): { east: number; north: number; de: number; dn: number; stretch: number } {
  const p = route.points, a = route.along;
  const n = a.length;
  s = Math.max(0, Math.min(route.length, s));
  let i = 0;
  // Binary search for the stretch holding s.
  let lo = 0, hi = n - 2;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (a[mid + 1] < s) lo = mid + 1; else { i = mid; hi = mid - 1; }
  }
  const x0 = p[i * 2], y0 = p[i * 2 + 1], dx = p[i * 2 + 2] - x0, dy = p[i * 2 + 3] - y0;
  const l = Math.hypot(dx, dy) || 1;
  const t = Math.max(0, Math.min(1, (s - a[i]) / l));
  return { east: x0 + dx * t, north: y0 + dy * t, de: dx / l, dn: dy / l, stretch: i };
}

/** How sharply the route turns within `span` metres ahead of `s`: radians. */
export function bendAhead(route: Route, s: number, span: number): number {
  const a = pointAt(route, s), b = pointAt(route, s + span * 0.5), c = pointAt(route, s + span);
  const h0 = Math.atan2(a.de, a.dn), h1 = Math.atan2(b.de, b.dn), h2 = Math.atan2(c.de, c.dn);
  const wrap = (x: number) => Math.atan2(Math.sin(x), Math.cos(x));
  return Math.max(Math.abs(wrap(h1 - h0)), Math.abs(wrap(h2 - h0)));
}

/** Standing at a stop line and pulling away again, seconds lost. */
const STOP_COST = 7;
/** Slowing to give way and getting back up to speed, seconds lost. */
const YIELD_COST = 3.5;
/** Where the route says nothing of speed: a town street's, m/s. */
const UNKNOWN_SPEED = 50 / 3.6;

/**
 * Seconds from `along` to the end, driving each stretch at its speed — near
 * enough the limit — and losing a little at every junction stopped or
 * slowed for.
 */
export function timeLeft(route: Route, along: number): number {
  const a = route.along;
  let t = 0;
  for (let i = 0; i + 1 < a.length; i++) {
    if (a[i + 1] <= along) continue;
    const len = a[i + 1] - Math.max(a[i], along);
    const v = route.speeds[i];
    t += len / (Number.isFinite(v) && v > 0.5 ? v * 0.92 : UNKNOWN_SPEED);
  }
  for (const j of route.junctions) if (j.at > along) t += j.stop ? STOP_COST : YIELD_COST;
  return t;
}

/**
 * How far along the route `seconds` of driving from `along` reaches — the
 * other way round from `timeLeft`, at the same speeds and with the same time
 * lost at each junction: the trip carried on while the game was closed.
 */
export function advanceAlong(route: Route, along: number, seconds: number): number {
  const a = route.along;
  let s = along, left = seconds;
  const junctions = route.junctions.filter(j => j.at > along).sort((x, y) => x.at - y.at);
  let next = 0;
  for (let i = 0; i + 1 < a.length && left > 0; i++) {
    if (a[i + 1] <= s) continue;
    const v = route.speeds[i];
    const speed = Number.isFinite(v) && v > 0.5 ? v * 0.92 : UNKNOWN_SPEED;
    const end = a[i + 1];
    while (left > 0 && s < end) {
      // Up to the next junction on this stretch, or its end.
      const stop = next < junctions.length && junctions[next].at <= end ? junctions[next].at : end;
      const need = (stop - s) / speed;
      if (need > left) return s + left * speed;
      left -= need;
      s = stop;
      if (next < junctions.length && junctions[next].at === stop) {
        left -= junctions[next].stop ? STOP_COST : YIELD_COST;
        next++;
      }
    }
  }
  return Math.min(route.length, s);
}
